/* eslint-disable @next/next/no-img-element */
import { LoadingOutlined } from "@ant-design/icons";
import { Empty, Skeleton, theme } from "antd";
import { DigestSection } from "pages/api/deep-links/types";
import { Hover } from "src/components/Hover";
import { useQuery } from "react-query";
import { getResultSummariesBulk } from "pages/api-clients/graph-search";
import { MarkdownRenderer } from "src/components/MarkdownRenderer";
import { ChatAssistant } from "src/search/components/ChatAssistant";
import { CSSProperties, useEffect, useState } from "react";
import { TextTruncated } from "src/components/TextTruncated";
import { DefaultPrefixKeys, PrefixGlobalKey } from "pages/api-clients/narrative-prefix";

export function DigestSectionCard(props: {
  orgId: string;
  section: DigestSection & { name: string };
}) {
  const { token } = theme.useToken();
  const [hasResults, setHasResults] = useState<boolean>(false);
  const [modal, setModal] = useState<JSX.Element | null>(null);

  const withOrgId = {
    prompt: props.section.prompt,
    queries: props.section.queries.map((q) => ({ ...q, orgId: props.orgId })),
  };

  const result = useQuery(
    [props.orgId, props.section],
    async () =>
      getResultSummariesBulk(
        { sections: [withOrgId] },
        props.orgId,
        DefaultPrefixKeys.NARRATIVE
      ),
    { enabled: Boolean(props.orgId) }
  );

  useEffect(() => {
    const queriesWithResults =
      result.data?.details?.filter((detail) => {
        return detail.queriesWithData > 0;
      })?.length || 0;
    setHasResults(queriesWithResults > 0);
  }, [result]);

  return (
    <div style={{ display: hasResults ? "block" : "none" }}>
      {modal}
      <Hover
        baseStyle={{
          backgroundColor: `${token.colorBgLayout}`,
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          gap: 8,
          padding: 10,
          borderRadius: 20,
          marginBottom: 20,
        }}
        hoverStyle={{
          cursor: "pointer",
          backgroundColor: token.colorBgContainer,
          outline: `solid 2px ${token.colorBorder}`,
          outlineOffset: 4,
          borderRadius: 20,
        }}
      >
        <div>
          <div
            style={{
              minWidth: 0,
              flex: 1,
              height: "100%",
              display: "flex",
              width: "100%",
              padding: 8,
              flexDirection: "column",
            }}
            onClick={() => {
              setModal(
                <ChatAssistant
                  title={props.section.name}
                  initialPrompt={props.section.prompt || ""}
                  dataSources={props.section.queries.map((query) => ({
                    query,
                    format: "csv" as const,
                  }))}
                  timeframe={{
                    duration: props.section.queries[0]?.duration || "P7D",
                  }}
                  onClose={() => setModal(null)}
                />
              );
            }}
          >
            {result.isLoading && (
              <>
                <h5 style={{ color: token.colorTextSecondary }}>
                  {props.section.name}
                </h5>
                <Skeleton active />
              </>
            )}
            <TextTruncated lines={10}>
              <MarkdownRenderer>{result.data?.summary}</MarkdownRenderer>
            </TextTruncated>
          </div>
          <div style={{ padding: "10px 0" }}>
            <div style={{ fontWeight: 500 }} className="line-clamp-2"></div>
          </div>
        </div>
      </Hover>
    </div>
  );
}
const capitalizeFirstLetter = (s: string) =>
  s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
